import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { sound } from "@/lib/sound";
import { HostControls } from "@/components/room/HostControls";
import { PhaseBanner } from "@/components/room/PhaseBanner";
import { PlayerList, RestartVote } from "@/components/room/PlayerList";
import { ReactionBar, ReactionFloats } from "@/components/room/Reactions";
import { RoomHeader } from "@/components/room/RoomHeader";
import { ResultsPanel, VotingPanel } from "@/components/room/RoundPanels";
import { StoryThread } from "@/components/room/StoryThread";
import { StartingPanel, WaitingPanel } from "@/components/room/WaitingPanel";
import { WritingPanel } from "@/components/room/WritingPanel";
import { YarnBall } from "@/components/yarn/YarnBall";
import { useRoomSocket } from "@/hooks/useRoomSocket";

const REMOVED = {
  kicked: ["You were kicked", "The host removed you from this game. You can rejoin if they let you back."],
  banned: ["You've been banned", "The host banned you from this game."],
  elsewhere: ["Opened somewhere else", "This game is open in another tab or device."],
};

function RemovedScreen({ removed, onBack }) {
  const [title, text] = REMOVED[removed.reason] || ["Can't join this game", removed.message];
  return (
    <div className="flex-1 grid place-items-center px-5">
      <div className="patch patch-stitched max-w-md w-full p-8 text-center" data-testid="removed-screen">
        <YarnBall size={56} color="#991B1B" className="mx-auto" />
        <h1 className="font-display text-3xl font-semibold mt-4" data-testid="removed-title">{title}</h1>
        <p className="text-[var(--yarn-muted)] mt-2" data-testid="removed-message">{text}</p>
        <button className="btn-yarn w-full h-14 mt-6 text-lg" onClick={onBack} data-testid="back-to-lobby-button">Back to lobby</button>
      </div>
    </div>
  );
}

function useWinMeow(state) {
  const done = useRef(null);
  useEffect(() => {
    const room = state?.room;
    const result = room?.phase === "results" && room.last_result;
    if (!result) return;
    const winner = result.entries.find((e) => e.id === result.winner_id);
    const key = `${room.story[0]?.text}-${result.round}`;
    if (winner && winner.uid === state.me && done.current !== key) {
      done.current = key;
      sound.meow();
    }
  }, [state]);
}

function PhasePanel({ room, isHost, send }) {
  switch (room.phase) {
    case "starting": return <StartingPanel />;
    case "writing": return <WritingPanel room={room} send={send} />;
    case "voting": return <VotingPanel room={room} send={send} />;
    case "results": return <ResultsPanel room={room} />;
    case "ended": return <WaitingPanel room={room} isHost={isHost} send={send} ended />;
    default: return <WaitingPanel room={room} isHost={isHost} send={send} />;
  }
}

export default function RoomPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { state, status, removed, offset, send, reactions } = useRoomSocket(code.toUpperCase());
  useWinMeow(state);

  useEffect(() => {
    const unlock = () => sound.unlock();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  if (removed) return <RemovedScreen removed={removed} onBack={() => navigate("/lobby")} />;
  if (!state) {
    return (
      <div className="flex-1 grid place-items-center" data-testid="room-loading">
        <div className="text-center">
          <YarnBall size={56} className="mx-auto yarn-spin" />
          <p className="font-display mt-4">Joining the circle...</p>
        </div>
      </div>
    );
  }

  const { room, me } = state;
  const isHost = room.host_id === me;
  const leave = () => {
    send("leave");
    navigate("/lobby");
  };
  const canRestart = ["writing", "voting", "results"].includes(room.phase);
  const moods = Object.fromEntries(reactions.map((r) => [r.user_id, r.kind]));

  return (
    <div className="flex-1" data-testid="room-page">
      <RoomHeader room={room} status={status} onLeave={leave} />
      <ReactionFloats reactions={reactions} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
        <section className="space-y-5 min-w-0">
          <PhaseBanner room={room} offset={offset} />
          <StoryThread story={room.story} />
          <PhasePanel room={room} isHost={isHost} send={send} />
          <ReactionBar me={me} players={room.players} send={send} />
        </section>
        <aside className="space-y-5">
          <PlayerList room={room} me={me} isHost={isHost} send={send} moods={moods} />
          {canRestart && <RestartVote room={room} me={me} send={send} />}
          {isHost && <HostControls room={room} send={send} />}
        </aside>
      </main>
    </div>
  );
}
